"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { CarouselDots } from "@/components/ui/CarouselDots";
import { StarIcon } from "@/components/icons/StarIcon";
import { StarRating } from "@/components/layout/StarRating";

type SocialTrustStatStoryblok = {
  value?: string;
  label?: string;
  sublabel?: string;
  /** `rating`: Stern ueber der Zahl. `highlight`: grosse blaue Zahl. */
  style?: "rating" | "highlight";
  _uid: string;
  component: "social_trust_stat";
  [k: string]: any;
};

type SocialTrustQuoteStoryblok = {
  text?: string;
  name?: string;
  image?: { filename?: string; alt?: string };
  rating?: string;
  _uid: string;
  component: "social_trust_quote";
  [k: string]: any;
};

type SocialTrustStoryblok = {
  stats?: SocialTrustStatStoryblok[];
  quotes?: SocialTrustQuoteStoryblok[];
  _uid: string;
  component: "social_trust";
  [k: string]: any;
};

type SocialTrustProps = {
  blok: SocialTrustStoryblok;
};

/*
 * Masse aus dem Mobile-Design (393px) ausgemessen:
 * - Bewertung: Stern 48px, 8px Abstand, Zahl 32px bold, Text 18px direkt darunter
 * - Hervorhebung: Zahl 58px bold in sky-100, 6px Abstand, Text 18px bold, Zusatz 14px
 * - zwischen den Bloecken, zur Seitenanzeige und zu den Karten jeweils 24px
 */
const Stat = ({ blok }: { blok: SocialTrustStatStoryblok }) => {
  if (blok.style === "highlight") {
    return (
      <div {...storyblokEditable(blok)} className="flex flex-col items-center text-center">
        <p className="text-[58px] font-bold leading-snug tracking-[-0.04em] text-sky-100">
          {blok.value}
        </p>
        {blok.label && <p className="mt-1.5 text-lg font-bold leading-default text-black">{blok.label}</p>}
        {blok.sublabel && <p className="text-sm leading-default text-black">{blok.sublabel}</p>}
      </div>
    );
  }

  return (
    <div {...storyblokEditable(blok)} className="flex flex-col items-center text-center">
      <StarIcon className="size-12 text-sky-75" />
      <p className="mt-2 text-2.5xl font-bold leading-snug tracking-snug text-black">{blok.value}</p>
      {blok.label && <p className="text-lg leading-default text-black">{blok.label}</p>}
      {blok.sublabel && <p className="text-sm leading-default text-black">{blok.sublabel}</p>}
    </div>
  );
};

const QuoteCard = ({ blok }: { blok: SocialTrustQuoteStoryblok }) => {
  // Gleicher Standard wie das Storyblok-Feld `rating`.
  const rating = Number(blok.rating || 5);

  return (
    <div
      {...storyblokEditable(blok)}
      className="flex flex-col bg-white rounded-2xl px-4 py-6 w-[320px] max-w-[85vw]"
    >
      <StarRating value={Number.isFinite(rating) ? rating : 5} />
      {blok.text && <p className="mt-6 text-copy leading-default text-black">{blok.text}</p>}
      {blok.image?.filename && (
        <Image
          src={blok.image.filename}
          alt={blok.image.alt || blok.name || ""}
          width={48}
          height={48}
          className="mt-6 w-12 h-12 rounded-full object-cover"
        />
      )}
      {blok.name && (
        <p className="mt-3 text-sm md:text-base font-bold leading-default text-black">{blok.name}</p>
      )}
    </div>
  );
};

const SocialTrust = ({ blok }: SocialTrustProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [snapCount, setSnapCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 4000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  // Punkte = Scroll-Positionen, nicht Karten: passen alle Karten auf den
  // Bildschirm, gibt es nur eine Position und keine Punkte.
  const syncSnaps = useCallback(() => {
    if (!api) return;
    setSnapCount(api.scrollSnapList().length);
    setActiveIndex(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    syncSnaps();
    api.on("select", syncSnaps);
    api.on("reInit", syncSnaps);
    return () => {
      api.off("select", syncSnaps);
      api.off("reInit", syncSnaps);
    };
  }, [api, syncSnaps]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    if (snapCount > 1 && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, snapCount, isInView, lightboxOpen]);

  return (
    <section
      ref={sectionRef}
      {...storyblokEditable(blok)}
      className="w-full bg-sand-10 pt-6 pb-12 md:pt-12"
    >
      {blok.stats && blok.stats.length > 0 && (
        <div className="flex flex-col items-center px-6 [&>*+*]:mt-6 md:flex-row md:justify-center md:items-center md:[&>*+*]:mt-0 md:[&>*+*]:ml-16">
          {blok.stats.map((stat) => (
            <Stat key={stat._uid} blok={stat} />
          ))}
        </div>
      )}

      <CarouselDots
        count={snapCount}
        activeIndex={activeIndex}
        onSelect={(index) => api?.scrollTo(index)}
        className="justify-center mt-6"
      />

      {blok.quotes && blok.quotes.length > 0 && (
        <Carousel
          setApi={setApi}
          opts={{ align: "start", containScroll: "trimSnaps", loop: false, watchResize: true }}
          plugins={[autoplayRef.current]}
          className="w-full mt-6"
        >
          {/* -ml-2 + Karten-pl-2 = 8px Abstand; pl-6 setzt die erste Karte 24px vom Rand */}
          <CarouselContent className="-ml-2 pl-6 md:pl-8 items-start">
            {blok.quotes.map((quote) => (
              <CarouselItem key={quote._uid} className="basis-auto pl-2">
                <QuoteCard blok={quote} />
              </CarouselItem>
            ))}
            <div className="shrink-0 w-6 md:w-8" />
          </CarouselContent>
        </Carousel>
      )}
    </section>
  );
};

export default SocialTrust;
